import React, { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "../api/adminApi";
import pageStyles from "../styles/PageLayout.module.css";
import panelStyles from "../styles/Panel.module.css";
import PageContent from "../shared/PageContent";
import PageHeader from "../shared/PageHeader";
import styles from "../styles/PromoCodesPage.module.css";

const PROMO_PRODUCTS = [
  { id: 1, label: "Lite", group: "subscription", defaultDays: 30 },
  { id: 2, label: "Pro", group: "subscription", defaultDays: 30 },
  { id: 3, label: "Max", group: "subscription", defaultDays: 30 },
  { id: 15, label: "Lite (7 дней)", group: "subscription", defaultDays: 7 },
  { id: 16, label: "Pro (7 дней)", group: "subscription", defaultDays: 7 },
  { id: 17, label: "Max (7 дней)", group: "subscription", defaultDays: 7 },
  { id: 9, label: "ИИ-фотосессия: 1 генерация", group: "bundle" },
  { id: 10, label: "ИИ-фотосессия: 6 генераций", group: "bundle" },
  { id: 11, label: "Решение по фото: 3000 токенов", group: "bundle" },
  { id: 12, label: "Решение по фото: 10", group: "bundle" },
  { id: 13, label: "Музыка: 1", group: "bundle" },
  { id: 14, label: "Музыка: 6", group: "bundle" },
  { id: 7, label: "Рулетка: 1 билет", group: "bundle" },
  { id: 8, label: "Рулетка: 5 билетов", group: "bundle" }
];

function formatPayloadSummary(result) {
  if (!result) return "";
  const parts = [
    `Код: ${result.code || "-"}`,
    `Использований: ${result.maxUses || 1}`,
    result.subscriptionDays ? `Подписка: ${result.subscriptionDays} дн.` : null,
    result.product?.description ? `Товар: ${result.product.description}` : null,
    result.product?.duration_sec ? `Срок: ${Math.round(Number(result.product.duration_sec) / 86400)} дн.` : null
  ].filter(Boolean);
  return parts.join("\n");
}

function formatPromoDate(value) {
  if (!value) return "—";
  try {
    return new Date(Number(value) * 1000).toLocaleString("ru-RU");
  } catch (_) {
    return "—";
  }
}

function isSubscriptionProduct(product) {
  return Boolean(
    product?.premium_plan_id ||
      product?.is_subscription ||
      Number(product?.duration_sec || 0) > 0 ||
      [1, 2, 3, 15, 16, 17].includes(Number(product?.id || 0))
  );
}

export default function PromoCodesPage({ adminToken, enabled = true }) {
  const [productId, setProductId] = useState(1);
  const [maxUses, setMaxUses] = useState(1);
  const [subscriptionDays, setSubscriptionDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [deletingCode, setDeletingCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  const selectedProduct = useMemo(
    () => PROMO_PRODUCTS.find((item) => Number(item.id) === Number(productId)) || PROMO_PRODUCTS[0],
    [productId]
  );
  const isSubscription = selectedProduct?.group === "subscription";

  const reload = useCallback(async () => {
    if (!enabled || !adminToken) return;
    setListLoading(true);
    try {
      const response = await adminApi.listPromoCodes(adminToken);
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
    } catch (err) {
      setItems([]);
      setError(err?.response?.data?.error || err?.message || "Не удалось загрузить промокоды");
    } finally {
      setListLoading(false);
    }
  }, [adminToken, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleCreate = async () => {
    if (!enabled || loading) return;
    setLoading(true);
    setError("");
    try {
      const response = await adminApi.createPromoCode(adminToken, {
        productId: Number(productId),
        maxUses: Math.max(1, Number(maxUses || 1)),
        subscriptionDays: isSubscription ? Math.max(0, Number(subscriptionDays || 0)) : 0
      });
      setResult(response.data);
      await reload();
    } catch (err) {
      setResult(null);
      setError(err?.response?.data?.error || err?.message || "Не удалось создать промокод");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.code || !navigator?.clipboard?.writeText) return;
    await navigator.clipboard.writeText(result.code);
  };

  const handleDelete = async (code) => {
    if (!code || deletingCode) return;
    const confirmed = window.confirm(`Удалить промокод ${code}?`);
    if (!confirmed) return;

    setDeletingCode(code);
    setError("");
    try {
      await adminApi.deletePromoCode(adminToken, code);
      if (result?.code === code) {
        setResult(null);
      }
      await reload();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Не удалось удалить промокод");
    } finally {
      setDeletingCode("");
    }
  };

  return (
    <div className={pageStyles.page}>
      <PageHeader
        title="Промокоды"
        description="Создавайте подарочные промокоды для тарифов и пакетов. Можно задать несколько использований, а для подписок - свой срок в днях."
      />
      <PageContent>
        <div className={styles.grid}>
          <section className={panelStyles.panel}>
            <div className={panelStyles.header}>
              <div>
                <h3 className={panelStyles.title}>Создать промокод</h3>
                <div className={panelStyles.note}>
                  Промокод можно активировать несколько раз, если указать `maxUses` больше 1.
                </div>
              </div>
            </div>

            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span>Товар / тариф</span>
                <select
                  className={panelStyles.select}
                  value={productId}
                  onChange={(event) => {
                    const nextId = Number(event.target.value);
                    setProductId(nextId);
                    const nextProduct = PROMO_PRODUCTS.find((item) => Number(item.id) === nextId);
                    if (nextProduct?.group === "subscription") {
                      setSubscriptionDays(nextProduct.defaultDays || 30);
                    } else {
                      setSubscriptionDays(0);
                    }
                  }}
                >
                  {PROMO_PRODUCTS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label} (id {item.id})
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span>Макс. использований</span>
                <input
                  className={panelStyles.input}
                  type="number"
                  min="1"
                  step="1"
                  value={maxUses}
                  onChange={(event) => setMaxUses(event.target.value)}
                />
              </label>

              <label className={styles.field}>
                <span>Срок подписки, дней</span>
                <input
                  className={panelStyles.input}
                  type="number"
                  min="0"
                  step="1"
                  value={subscriptionDays}
                  disabled={!isSubscription}
                  onChange={(event) => setSubscriptionDays(event.target.value)}
                />
              </label>
            </div>

            <div className={styles.actions}>
              <button type="button" className={panelStyles.button} onClick={handleCreate} disabled={loading}>
                {loading ? "Создаём..." : "Создать промокод"}
              </button>
              <button type="button" className={panelStyles.buttonSecondary} onClick={reload} disabled={listLoading}>
                {listLoading ? "Обновляем..." : "Обновить список"}
              </button>
            </div>

            <div className={panelStyles.note}>
              Подсказка: для подписок можно поставить любой срок, а для товарных пакетов оставьте 0.
            </div>

            {error ? <div className={panelStyles.error}>{error}</div> : null}
          </section>

          <section className={panelStyles.panel}>
            <div className={panelStyles.header}>
              <div>
                <h3 className={panelStyles.title}>Результат</h3>
                <div className={panelStyles.note}>Готовый промокод и параметры будут показаны здесь.</div>
              </div>
              {result?.code ? (
                <button type="button" className={panelStyles.buttonSecondary} onClick={handleCopy}>
                  Скопировать код
                </button>
              ) : null}
            </div>

            {result?.code ? (
              <div className={styles.resultCard}>
                <div className={styles.resultCode}>{result.code}</div>
                <pre className={styles.resultMeta}>{formatPayloadSummary(result)}</pre>
                <div className={styles.resultHint}>
                  Активация уходит в `APP_CHAT_ID`, а покупка промокода по-прежнему отображается в `DONUT_CHAT_ID`.
                </div>
              </div>
            ) : (
              <div className={panelStyles.note}>Пока промокод не создан.</div>
            )}
          </section>
        </div>

        <section className={panelStyles.panel} style={{ marginTop: 18 }}>
          <div className={panelStyles.header}>
            <div>
              <h3 className={panelStyles.title}>Активные промокоды</h3>
              <div className={panelStyles.note}>
                Здесь показываются все созданные коды. Использованные и просроченные тоже видны, чтобы их можно было удалить.
              </div>
            </div>
            <button type="button" className={panelStyles.buttonSecondary} onClick={reload} disabled={listLoading}>
              {listLoading ? "Обновляем..." : "Обновить"}
            </button>
          </div>

          {items.length > 0 ? (
            <div className={styles.promoList}>
              {items.map((item) => {
                const isDeleting = deletingCode === item.code;
                const statusClass =
                  item.status === "active"
                    ? styles.statusActive
                    : item.status === "used"
                      ? styles.statusUsed
                      : styles.statusExpired;

                return (
                  <div key={item.code} className={styles.promoCard}>
                    <div className={styles.promoTop}>
                      <div>
                        <div className={styles.promoCode}>{item.code}</div>
                        <div className={styles.promoMeta}>
                          {item.product?.description || `Товар ID ${item.productId || "-"}`}
                        </div>
                      </div>
                      <span className={`${styles.statusPill} ${statusClass}`}>{item.status}</span>
                    </div>

                    <div className={styles.promoDetails}>
                      <div>Использований: {item.uses}/{item.maxUses}</div>
                      <div>Осталось: {item.remainingUses}</div>
                      <div>Срок подписки: {item.subscriptionDays || "—"}</div>
                      <div>Создан: {formatPromoDate(item.createdAt)}</div>
                      <div>Обновлён: {formatPromoDate(item.updatedAt)}</div>
                      <div>Истекает: {item.expiresAt ? formatPromoDate(item.expiresAt) : "—"}</div>
                    </div>

                    <div className={styles.promoActions}>
                      <button
                        type="button"
                        className={panelStyles.buttonSecondary}
                        onClick={() => handleDelete(item.code)}
                        disabled={isDeleting}
                      >
                        {isDeleting ? "Удаляем..." : "Удалить"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={panelStyles.note}>Пока промокодов нет.</div>
          )}
        </section>
      </PageContent>
    </div>
  );
}
